import React, {useEffect, useState} from "react";
import {Button, Col, Descriptions, Popover, Row, Tooltip} from "antd";
import {
    ReloadOutlined,
    PlusOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    EditOutlined
} from "@ant-design/icons";
import {useStore} from "../../../../hooks/useStore";
import moment from "moment";
import {IntegrationCreateModal} from "../modals/integrationCreateModal/IntegrationCreateModal";
import {IntegrationUpdateModal} from "../modals/integrationUpdateModal/IntegrationUpdateModal";
import {HideableText} from "../../../_defaultComponents/HideableText/HideableText";
import {ExpandableStyledTable} from "../../../_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";
import JSONInput from "react-json-editor-ajrm";
import {useTranslation} from "react-i18next";
import styled from "styled-components";

const ExpandedDescriptionsStyled = styled(Descriptions)`
    .ant-descriptions-item-label {
        text-align: left;
    }
    .ant-descriptions-item-content {
        text-align: right;
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }
    .integration-secret-box {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
    }
`;

export const SubcompanyIntegrationList = ({subcompanyId, ...props}) => {
    const {t, i18n} = useTranslation(["integrations", "paymentTypes"]);
    const stores = useStore();
    const [integrations, setIntegrations] = useState([]);
    const [integrationsLoading, setIntegrationsLoading] = useState(true);
    const [integrationsFields, setIntegrationsFields] = useState([]);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [paymentTypeRulesByIntegration, setPaymentTypeRulesByIntegration] = useState({});
    const [integrationFieldsLoading, setIntegrationFieldsLoading] = useState(true);
    const [paymentTypesLoading, setPaymentTypesLoading] = useState(true);
    const [selectedIntegration, setSelectedIntegration] = useState();

    const [createIntegrationModalVisibility, setCreateIntegrationModalVisibility] = useState(false);

    const [updateIntegrationModalVisibility, setUpdateIntegrationModalVisibility] = useState(false);

    const loadIntegrations = async () => {
        setIntegrationsLoading(true);
        const subcompanyIntegrationsResponse = await stores.companyStore.listSubcompanyIntegrations(
            {subCompanyId: subcompanyId}
        );
        setIntegrations(subcompanyIntegrationsResponse.data);
        setIntegrationsLoading(false);
    };

    const loadIntegrationsFields = async () => {
        setIntegrationFieldsLoading(true);
        const integrationsFieldsResponse = await stores.companyStore.listSubcompanyIntegrationsFields();
        setIntegrationsFields(integrationsFieldsResponse.data);
        setIntegrationFieldsLoading(false);
    };

    const loadIntegrationsPaymentTypes = async () => {
        setPaymentTypesLoading(true);
        try {
            const integrationsPaymentTypesResponse = await stores.companyStore.listIntegrationsPaymentTypes();
            if (!integrationsPaymentTypesResponse.success) {
                throw new Error();
            }
            const {
                integrationsPaymentTypes = {},
                paymentTypes = []
            } = integrationsPaymentTypesResponse.data;
            setPaymentTypeRulesByIntegration(integrationsPaymentTypes);
            setPaymentTypes(paymentTypes);
        } catch (e) {
        } finally {
            setPaymentTypesLoading(false);
        }
    };

    const handleAddIntegration = () => {
        setCreateIntegrationModalVisibility(true);
    };

    const closeModals = async (needRefresh = false) => {
        setCreateIntegrationModalVisibility(false);
        setUpdateIntegrationModalVisibility(false);
        setSelectedIntegration(undefined);
        if (needRefresh) {
            await loadIntegrations();
        }
    };

    useEffect(() => {
        loadIntegrations();
        loadIntegrationsFields();
        loadIntegrationsPaymentTypes();
    }, []);

    const handleSelectIntegration = integration => {
        setSelectedIntegration(integration);
        setUpdateIntegrationModalVisibility(true);
    };

    const healthCheckIntegration = async integrationIndex => {
        const integration = integrations[integrationIndex];
        if (!integration) return;

        integrations[integrationIndex].healtCheckLoading = true;
        setIntegrations([...integrations]);

        try {
            const healthCheckResponse = await stores.companyStore.healthCheckSubcompanyIntegration({
                subCompanyId: subcompanyId,
                integrationType: integration.type,
                integrationId: integration?.isMultiple && integration?._id
            });
            delete integrations[integrationIndex].healtCheckLoading;
            integrations[integrationIndex].isValid = healthCheckResponse?.data?.isValid;
            setIntegrations([...integrations]);
        } catch (error) {
            delete integrations[integrationIndex].healtCheckLoading;
            setIntegrations([...integrations]);
        }
    };

    return (
        <>
            <Col {...props}>
                <Row
                    type="flex"
                    justify="space-between"
                    style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        marginTop: "30px",
                        marginBottom: "20px"
                    }}
                >
                    {t("integrations.others.integrations", {ns: "integrations"})}
                    <Row type="flex" justify="end" gutter={4}>
                        <Tooltip title={t("integrations.actions.refresh", {ns: "integrations"})}>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    onClick={loadIntegrations}
                                    loading={integrationsLoading && integrationFieldsLoading}
                                />
                            </Col>
                        </Tooltip>
                        {stores.meStore.checkPermission("subcompanies.manageIntegrations") && (
                            <Tooltip
                                placement="topRight"
                                title={t("integrations.actions.add", {ns: "integrations"})}
                            >
                                <Col>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddIntegration}
                                        loading={integrationsLoading && integrationFieldsLoading}
                                    />
                                </Col>
                            </Tooltip>
                        )}
                    </Row>
                </Row>
                <Row>
                    <Col span={24}>
                        <ExpandableStyledTable
                            columns={[
                                {
                                    title: t("integrations.commonFields.type", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "type",
                                    key: "type",
                                    onCell: () => ({
                                        "data-label": t("integrations.commonFields.type", {
                                            ns: "integrations"
                                        })
                                    }),
                                    render: platform =>
                                        t(`integrations.platforms.${platform}.name`, {
                                            ns: "integrations"
                                        })
                                },
                                {
                                    title: t("integrations.commonFields.isValid", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "isValid",
                                    key: "isValid",
                                    onCell: () => ({
                                        "data-label": t("integrations.commonFields.isValid", {
                                            ns: "integrations"
                                        })
                                    }),
                                    render: isValid => (
                                        <span style={{display: "block", textAlign: "right"}}>
                                            {isValid ? (
                                                <b style={{color: "#7E9E34"}}>
                                                    {t("integrations.commonFields.verified", {
                                                        ns: "integrations"
                                                    })}
                                                </b>
                                            ) : (
                                                <b style={{color: "#C42F20"}}>
                                                    {t("integrations.commonFields.notVerified", {
                                                        ns: "integrations"
                                                    })}
                                                </b>
                                            )}
                                        </span>
                                    )
                                },
                                {
                                    title: t("integrations.commonFields.lastWorkingTime", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "lastWorkLog",
                                    key: "lastWorkLog",
                                    onCell: () => ({
                                        "data-label": t(
                                            "integrations.commonFields.lastWorkingTime",
                                            {
                                                ns: "integrations"
                                            }
                                        )
                                    }),
                                    render: lastWorkLog => (
                                        <>
                                            {lastWorkLog && lastWorkLog.lastWorkingTime ? (
                                                <>
                                                    <Tooltip
                                                        title={moment(
                                                            lastWorkLog.lastWorkingTime
                                                        ).format("DD.MM.YYYY HH:mm")}
                                                    >
                                                        {moment
                                                            .duration(
                                                                moment(
                                                                    lastWorkLog.lastWorkingTime
                                                                ).diff(moment())
                                                            )
                                                            .locale(
                                                                i18n.resolvedLanguage === "tr"
                                                                    ? "tr"
                                                                    : "en"
                                                            )
                                                            .humanize(true)}
                                                    </Tooltip>
                                                    <Popover
                                                        content={
                                                            <JSONInput
                                                                id="courierLogGPSProviderJSON"
                                                                placeholder={lastWorkLog}
                                                                theme="dark_mitsuketa_tribute"
                                                                width="auto"
                                                                height="auto"
                                                                viewOnly={true}
                                                                confirmGood={false}
                                                            />
                                                        }
                                                    >
                                                        <InfoCircleOutlined
                                                            style={{paddingLeft: "5px"}}
                                                        />
                                                    </Popover>
                                                    {moment().diff(
                                                        moment(lastWorkLog.lastWorkingTime),
                                                        "minutes"
                                                    ) > 10 && (
                                                        <>
                                                            <Tooltip
                                                                title={t(
                                                                    "integrations.others.longTimeNotWorking",
                                                                    {ns: "integrations"}
                                                                )}
                                                            >
                                                                <ExclamationCircleOutlined
                                                                    style={{
                                                                        paddingLeft: "5px",
                                                                        color: "#C42F20"
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                t("integrations.others.notWorkingYet", {
                                                    ns: "integrations"
                                                })
                                            )}
                                        </>
                                    )
                                },
                                {
                                    title: t("integrations.commonFields.isActive", {
                                        ns: "integrations"
                                    }),
                                    dataIndex: "isActive",
                                    key: "isActive",
                                    onCell: () => ({
                                        "data-label": t("integrations.commonFields.isActive", {
                                            ns: "integrations"
                                        })
                                    }),
                                    render: isActive =>
                                        isActive
                                            ? t("other.active", {ns: "common"})
                                            : t("other.passive", {ns: "common"})
                                },
                                {
                                    title: t("actions.action", {ns: "common"}),
                                    dataIndex: "_actions",
                                    key: "actions",
                                    onCell: () => ({
                                        "data-label": t("actions.action", {ns: "common"})
                                    }),
                                    render: (field, integration, idx) => (
                                        <Row type="flex" justify="end" gutter={4}>
                                            <Col>
                                                {stores.meStore.checkPermission(
                                                    "subcompanies.manageIntegrations"
                                                ) && (
                                                    <Tooltip
                                                        title={t(
                                                            "integrations.actions.healthCheck",
                                                            {
                                                                ns: "integrations"
                                                            }
                                                        )}
                                                    >
                                                        <Button
                                                            loading={integration?.healtCheckLoading}
                                                            icon={<ReloadOutlined />}
                                                            onClick={() =>
                                                                healthCheckIntegration(idx)
                                                            }
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Col>
                                            <Col>
                                                {stores.meStore.checkPermission(
                                                    "subcompanies.manageIntegrations"
                                                ) && (
                                                    <Tooltip
                                                        title={t("actions.edit", {ns: "common"})}
                                                    >
                                                        <Button
                                                            icon={<EditOutlined />}
                                                            onClick={() =>
                                                                handleSelectIntegration(integration)
                                                            }
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Col>
                                        </Row>
                                    )
                                }
                            ]}
                            dataSource={integrations}
                            loading={integrationsLoading}
                            pagination={false}
                            rowKey={(_, index) => index.toString()}
                            locale={{
                                emptyText: t("integrations.others.noIntegrationFound", {
                                    ns: "integrations"
                                })
                            }}
                            expandable={{
                                expandedRowRender: record => (
                                    <ExpandedDescriptionsStyled bordered size="small">
                                        <Descriptions.Item
                                            label={t(`integrations.commonFields.type`, {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {t(`integrations.platforms.${record.type}.name`, {
                                                ns: "integrations"
                                            })}
                                        </Descriptions.Item>
                                        {Object.entries(record)
                                            .filter(
                                                entry =>
                                                    ![
                                                        "isValid",
                                                        "canCreateOrder",
                                                        "canRunCallbacks",
                                                        "isActive",
                                                        "lastWorkLog",
                                                        "type",
                                                        "eventBridgeRuleName",
                                                        "supportedPaymentTypes"
                                                    ].includes(entry[0])
                                            )
                                            .map(entry => (
                                                <Descriptions.Item
                                                    label={t(
                                                        `integrations.platforms.${record.type}.fields.${entry[0]}`,
                                                        {
                                                            ns: "integrations"
                                                        }
                                                    )}
                                                    span={3}
                                                    key={`expandedItemField${record?.type}${entry[0]}`}
                                                >
                                                    <span className="integration-secret-box">
                                                        <HideableText
                                                            value={entry?.[1].toString()}
                                                            defaultHidden={true}
                                                        />
                                                    </span>
                                                </Descriptions.Item>
                                            ))}
                                        <Descriptions.Item
                                            label={t(
                                                "integrations.commonFields.supportedPaymentTypes",
                                                {
                                                    ns: "integrations"
                                                }
                                            )}
                                            span={3}
                                        >
                                            {record?.supportedPaymentTypes?.length > 0
                                                ? record.supportedPaymentTypes
                                                      .map(paymentType =>
                                                          t(`${paymentType}`, {
                                                              ns: "paymentTypes"
                                                          })
                                                      )
                                                      .join(", ")
                                                : "-"}
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("integrations.commonFields.isActive", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record.isActive
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("integrations.commonFields.canCreateOrder", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record.canCreateOrder
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("integrations.commonFields.canRunCallbacks", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record.canRunCallbacks
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </Descriptions.Item>

                                        <Descriptions.Item
                                            label={t("integrations.commonFields.isValid", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record.isValid ? (
                                                <b style={{color: "#7E9E34"}}>
                                                    {t("integrations.commonFields.verified", {
                                                        ns: "integrations"
                                                    })}
                                                </b>
                                            ) : (
                                                <b style={{color: "#C42F20"}}>
                                                    {t("integrations.commonFields.notVerified", {
                                                        ns: "integrations"
                                                    })}
                                                </b>
                                            )}
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("integrations.commonFields.lastWorkingTime", {
                                                ns: "integrations"
                                            })}
                                            span={3}
                                        >
                                            {record?.lastWorkLog &&
                                            record?.lastWorkLog?.lastWorkingTime ? (
                                                <>
                                                    <Tooltip
                                                        title={moment(
                                                            record.lastWorkLog.lastWorkingTime
                                                        ).format("DD.MM.YYYY HH:mm")}
                                                    >
                                                        {moment
                                                            .duration(
                                                                moment(
                                                                    record.lastWorkLog
                                                                        .lastWorkingTime
                                                                ).diff(moment())
                                                            )
                                                            .locale(
                                                                i18n.resolvedLanguage === "tr"
                                                                    ? "tr"
                                                                    : "en"
                                                            )
                                                            .humanize(true)}
                                                    </Tooltip>
                                                    <Popover
                                                        content={
                                                            <JSONInput
                                                                id="courierLogGPSProviderJSON"
                                                                placeholder={record.lastWorkLog}
                                                                theme="dark_mitsuketa_tribute"
                                                                width="auto"
                                                                height="auto"
                                                                viewOnly={true}
                                                                confirmGood={false}
                                                            />
                                                        }
                                                    >
                                                        <InfoCircleOutlined
                                                            style={{paddingLeft: "5px"}}
                                                        />
                                                    </Popover>
                                                    {moment().diff(
                                                        moment(record.lastWorkLog?.lastWorkingTime),
                                                        "minutes"
                                                    ) > 10 && (
                                                        <Tooltip
                                                            title={t(
                                                                "integrations.others.longTimeNotWorking",
                                                                {ns: "integrations"}
                                                            )}
                                                        >
                                                            <ExclamationCircleOutlined
                                                                style={{
                                                                    paddingLeft: "5px",
                                                                    color: "#C42F20"
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </>
                                            ) : (
                                                t("integrations.others.notWorkingYet", {
                                                    ns: "integrations"
                                                })
                                            )}
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "integrations.commonFields.eventBridgeRuleName",
                                                {
                                                    ns: "integrations"
                                                }
                                            )}
                                            span={3}
                                        >
                                            {record?.eventBridgeRuleName ? (
                                                <span className="integration-secret-box">
                                                    <HideableText
                                                        value={record?.eventBridgeRuleName}
                                                        defaultHidden={true}
                                                    />
                                                </span>
                                            ) : (
                                                t("integrations.others.noRuleFound", {
                                                    ns: "integrations"
                                                })
                                            )}
                                        </Descriptions.Item>
                                    </ExpandedDescriptionsStyled>
                                ),
                                rowExpandable: () => true
                            }}
                        />
                    </Col>
                </Row>
            </Col>
            <IntegrationCreateModal
                existIntegrations={integrations}
                subcompanyId={subcompanyId}
                integrationsFields={integrationsFields}
                visibility={createIntegrationModalVisibility}
                onCancel={closeModals}
                paymentTypes={paymentTypes}
                paymentTypeRules={paymentTypeRulesByIntegration}
            />
            <IntegrationUpdateModal
                subcompanyId={subcompanyId}
                selectedIntegration={selectedIntegration}
                integrationsFields={integrationsFields}
                visibility={updateIntegrationModalVisibility}
                onCancel={closeModals}
                paymentTypes={paymentTypes}
                paymentTypeRules={paymentTypeRulesByIntegration}
            />
        </>
    );
};
